'use client';

import * as React from 'react';
import Image from 'next/image';
import NiceModal from '@ebay/nice-modal-react';
import {
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Clock,
  DollarSign,
  Download,
  Maximize2,
  XCircle
} from 'lucide-react';
import { useFormContext } from 'react-hook-form';

import { Alert, AlertDescription } from '@workspace/ui/components/alert';
import { Badge } from '@workspace/ui/components/badge';
import { Button } from '@workspace/ui/components/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle
} from '@workspace/ui/components/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '@workspace/ui/components/collapsible';
import { Separator } from '@workspace/ui/components/separator';
import { Spinner } from '@workspace/ui/components/spinner';
import { cn } from '@workspace/ui/lib/utils';

import { GENERATOR_STEP_CONFIGS } from '~/components/generator/core/generator-step-config';
import { FullscreenImageModal } from '~/components/generator/shared/fullscreen-image-modal';
import { downloadUrlAsFile, downloadUrlsAsZip } from '~/lib/downloads';
import type { CompleteGeneratorSchema } from '~/schemas/generator/complete-generator-schema';

const loadingMessages = [
  'Analyzing the garments...',
  'Selecting the model...',
  'Compositing the image with AI...',
  'Adjusting for lighting and shadows...',
  'Rendering the final look...',
  'Almost ready...'
];

type GenerationResult = {
  data?: {
    images: string[];
    complete: boolean;
    redirect: string | null;
    successfulGenerations: number;
    failedGenerations: number;
  };
  error: string | null;
  serverError: string | null;
  validationErrors: string | null;
  code?:
    | 'REQUIRE_AUTH'
    | 'INSUFFICIENT_CREDITS'
    | 'FAILED_TO_DEDUCT_CREDITS'
    | 'GENERATION_FAILED'
    | 'PARTIAL_GENERATION';
  generationErrors?: Array<{
    combination: string;
    error: string;
    retryable: boolean;
    suggestion?: string;
  }>;
  quotaStatus?: {
    requestsToday: number;
    consecutiveFailures: number;
    isLikelyQuotaLimited: boolean;
  };
};

export function GeneratorOverviewStep({
  generatedImages = [],
  generationResult = null,
  onSubmit,
  isSubmitting = false,
  estimatedCreditCost = 0
}: {
  generatedImages?: string[];
  generationResult?: GenerationResult | null;
  onSubmit?: () => void;
  isSubmitting?: boolean;
  estimatedCreditCost?: number;
}): React.JSX.Element {
  const methods = useFormContext<CompleteGeneratorSchema>();
  const watchedValues = methods.watch();
  const formIsSubmitting = methods.formState.isSubmitting;

  const [messageIndex, setMessageIndex] = React.useState(0);
  const [showErrors, setShowErrors] = React.useState(false);

  React.useEffect(() => {
    if (!isSubmitting && !formIsSubmitting) return;

    const interval = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % loadingMessages.length);
    }, 1500);

    return () => clearInterval(interval);
  }, [isSubmitting, formIsSubmitting]);

  const openImageModal = (index: number) => {
    NiceModal.show(FullscreenImageModal, {
      images: generatedImages.map((url, idx) => ({
        url,
        title: `Generated Image ${idx + 1}`,
        filename: `generated-image-${new Date().toISOString().split('T')[0]}-${idx + 1}.jpg`
      })),
      currentIndex: index
    });
  };

  const downloadImage = async (imageUrl: string, index: number) => {
    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `generated-image-${timestamp}-${index + 1}.jpg`;
    await downloadUrlAsFile(imageUrl, filename).catch(() => {});
  };

  const downloadAllImages = async () => {
    const timestamp = new Date().toISOString().split('T')[0];
    const items = generatedImages.map((url, index) => ({
      url,
      name: `generated-image-${timestamp}-${index + 1}.jpg`
    }));
    await downloadUrlsAsZip(items, `generated-images-${timestamp}.zip`).catch(
      async () => {
        for (let i = 0; i < generatedImages.length; i++) {
          // small delay between downloads

          await new Promise((resolve) => setTimeout(resolve, 300));

          await downloadImage(generatedImages[i], i);
        }
      }
    );
  };

  const modelsCount = watchedValues.modelStep?.modelVariants?.length ?? 0;
  const posesCount = watchedValues.poseStep?.poses?.length ?? 0;
  const backgroundsCount =
    watchedValues.backgroundStep?.backgrounds?.length ?? 0;

  // Get actual results from generation
  const actualCreditsUsed = generationResult?.data?.successfulGenerations ?? 0;
  const successfulGenerations =
    generationResult?.data?.successfulGenerations ?? 0;
  const failedGenerations = generationResult?.data?.failedGenerations ?? 0;
  const hasErrors =
    generationResult?.generationErrors &&
    generationResult.generationErrors.length > 0;
  const isPartialSuccess = generationResult?.code === 'PARTIAL_GENERATION';
  const isCompleteFailure = generationResult?.code === 'GENERATION_FAILED';

  // Note: images are provided by the parent after submission completes

  return (
    <div className="grid h-full grid-cols-1 gap-8 md:grid-cols-[1fr_24rem]">
      {/* Main Content: Placeholder, Loading, or Results */}
      <Card
        className={cn(
          'flex max-h-[calc(100vh-8rem)] min-h-[32rem] flex-col',
          !generatedImages.length && 'hidden md:flex'
        )}
      >
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Generated Images</CardTitle>
          {generatedImages.length > 1 && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                downloadAllImages();
              }}
              className="flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              Download All ({generatedImages.length})
            </Button>
          )}
        </CardHeader>
        <CardContent className="flex flex-1 items-center justify-center overflow-y-auto">
          {isSubmitting || formIsSubmitting ? (
            <div className="flex flex-col items-center justify-center gap-4 text-center">
              <Spinner size="large" />
              <p className="text-sm text-muted-foreground">
                {loadingMessages[messageIndex]}
              </p>
            </div>
          ) : generatedImages.length > 0 ? (
            <div className="space-y-4">
              {/* Dynamic grid layout based on number of images */}
              <div
                className={cn(
                  'grid gap-4',
                  // Single image: full width with max width constraint
                  generatedImages.length === 1 &&
                    'grid-cols-1 justify-center max-w-2xl mx-auto',
                  // Two images: side by side
                  generatedImages.length === 2 &&
                    'grid-cols-1 md:grid-cols-2 max-w-4xl mx-auto',
                  // Three images: triangle layout
                  generatedImages.length === 3 &&
                    'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 max-w-5xl mx-auto',
                  // Four images: 2x2 grid
                  generatedImages.length === 4 &&
                    'grid-cols-1 md:grid-cols-2 max-w-4xl mx-auto',
                  // Five or more: adaptive grid
                  generatedImages.length >= 5 &&
                    'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4'
                )}
              >
                {generatedImages.map((src, idx) => (
                  <div
                    key={`${src}-${idx}`}
                    className={cn(
                      'group relative overflow-hidden rounded-lg border bg-muted shadow-sm transition-all duration-200 hover:shadow-lg',
                      // Single image gets special treatment
                      generatedImages.length === 1 && 'aspect-square max-w-lg',
                      // Two images get equal treatment
                      generatedImages.length === 2 && 'aspect-square',
                      // Three images: first one spans two columns on larger screens
                      generatedImages.length === 3 &&
                        idx === 0 &&
                        'aspect-square md:col-span-2 md:row-span-2',
                      generatedImages.length === 3 &&
                        idx > 0 &&
                        'aspect-square',
                      // Four or more: standard aspect ratio
                      generatedImages.length >= 4 && 'aspect-square'
                    )}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                  >
                    <div
                      className="cursor-pointer h-full w-full"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        openImageModal(idx);
                      }}
                    >
                      <Image
                        width={500}
                        height={500}
                        src={src}
                        alt={`Generated ${idx + 1}`}
                        className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-105"
                        loading="lazy"
                      />
                    </div>

                    {/* Hover overlay with controls */}
                    <div className="absolute inset-0 bg-black/0 transition-colors duration-200 group-hover:bg-black/10 pointer-events-none" />

                    {/* Image number */}
                    <div
                      className="absolute top-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                    >
                      {idx + 1}
                    </div>

                    {/* Action buttons */}
                    <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      {/* Expand icon */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          openImageModal(idx);
                        }}
                        className="bg-black/50 hover:bg-black/70 text-white p-1 rounded-md transition-colors duration-200"
                        aria-label="View fullscreen"
                        title="View fullscreen"
                      >
                        <Maximize2 className="h-3 w-3" />
                      </button>
                    </div>

                    {/* Download button - bottom center */}
                    <div className="absolute bottom-2 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          downloadImage(src, idx);
                        }}
                        className="bg-white/80 hover:bg-white text-black px-2 py-1 rounded-md transition-colors duration-200 flex items-center gap-1"
                        aria-label="Download image"
                        title="Download image"
                      >
                        <Download className="h-3 w-3" />
                        <span className="text-xs">Download</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Show error information if there are failures */}
              {hasErrors && (
                <div className="space-y-3">
                  <Separator />
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-amber-500" />
                    <h4 className="text-sm font-medium text-amber-700">
                      {failedGenerations} generation
                      {failedGenerations !== 1 ? 's' : ''} failed
                    </h4>
                  </div>

                  <Collapsible
                    open={showErrors}
                    onOpenChange={setShowErrors}
                  >
                    <CollapsibleTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full"
                      >
                        {showErrors ? 'Hide' : 'Show'} Error Details
                        {showErrors ? (
                          <ChevronUp className="ml-2 h-4 w-4" />
                        ) : (
                          <ChevronDown className="ml-2 h-4 w-4" />
                        )}
                      </Button>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="space-y-2 pt-2">
                      {generationResult?.generationErrors?.map((error, idx) => (
                        <Alert
                          key={idx}
                          variant={error.retryable ? 'default' : 'destructive'}
                        >
                          <AlertDescription className="space-y-1">
                            <div className="font-medium">
                              {error.combination}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {error.error}
                            </div>
                            {error.suggestion && (
                              <div className="text-sm text-blue-600">
                                💡 {error.suggestion}
                              </div>
                            )}
                          </AlertDescription>
                        </Alert>
                      ))}
                    </CollapsibleContent>
                  </Collapsible>
                </div>
              )}
            </div>
          ) : isCompleteFailure ? (
            <div className="flex flex-col items-center justify-center gap-4 text-center">
              <XCircle className="h-12 w-12 text-red-500" />
              <div className="space-y-2">
                <p className="text-lg font-medium text-red-700">
                  Generation Failed
                </p>
                <p className="text-sm text-muted-foreground">
                  {generationResult?.error}
                </p>
                {hasErrors && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowErrors(!showErrors)}
                  >
                    {showErrors ? 'Hide' : 'Show'} Error Details
                  </Button>
                )}
              </div>

              {showErrors && hasErrors && (
                <div className="w-full space-y-2">
                  {generationResult?.generationErrors?.map((error, idx) => (
                    <Alert
                      key={idx}
                      variant="destructive"
                    >
                      <AlertDescription className="space-y-1">
                        <div className="font-medium">{error.combination}</div>
                        <div className="text-sm">{error.error}</div>
                        {error.suggestion && (
                          <div className="text-sm text-blue-600">
                            💡 {error.suggestion}
                          </div>
                        )}
                      </AlertDescription>
                    </Alert>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="text-center text-sm text-muted-foreground">
              <p>Your generated images will appear here.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Right Panel: Overview and Actions */}
      <div className="flex max-h-[calc(100vh-8rem)] flex-col gap-6">
        <Card className="flex flex-grow flex-col overflow-y-auto">
          <CardHeader>
            <CardTitle>Overview</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {GENERATOR_STEP_CONFIGS.map((config) => {
              const selectedItems = methods.getValues(config.fieldPath) || [];
              return (
                <div
                  key={config.step}
                  className="space-y-2"
                >
                  <div className="flex items-center gap-2">
                    <config.selectedItemsIcon className="h-4 w-4 text-muted-foreground" />
                    <h4 className="text-sm font-medium">
                      {config.selectedItemsLabel}
                    </h4>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {selectedItems.length > 0 ? (
                      selectedItems.map((item) => (
                        <div
                          key={item.id}
                          data-item-id={item.id}
                        >
                          <config.SelectedItemCardComponent
                            item={item as any}
                          />
                        </div>
                      ))
                    ) : (
                      <div className="text-xs text-muted-foreground">
                        None selected
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>

        <Card className="flex-shrink-0">
          <CardContent className="p-4">
            <h3 className="mb-3 text-base font-semibold">Credits</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Models</span>
                <span>{modelsCount}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Poses</span>
                <span>{posesCount}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Backgrounds</span>
                <span>{backgroundsCount}</span>
              </div>
              <Separator className="my-2" />

              {/* Show estimated vs actual credits */}
              {generationResult ? (
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      Estimated Cost
                    </span>
                    <span>{estimatedCreditCost} credits</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Credits Used</span>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-green-600">
                        {actualCreditsUsed}
                      </span>
                      {actualCreditsUsed < estimatedCreditCost && (
                        <Badge
                          variant="secondary"
                          className="text-xs"
                        >
                          Saved {estimatedCreditCost - actualCreditsUsed}
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* Generation Results Summary */}
                  <Separator className="my-2" />
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Results</span>
                    <div className="flex items-center gap-2">
                      {successfulGenerations > 0 && (
                        <div className="flex items-center gap-1 text-green-600">
                          <CheckCircle className="h-3 w-3" />
                          <span className="text-xs">
                            {successfulGenerations}
                          </span>
                        </div>
                      )}
                      {failedGenerations > 0 && (
                        <div className="flex items-center gap-1 text-red-600">
                          <XCircle className="h-3 w-3" />
                          <span className="text-xs">{failedGenerations}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex justify-between font-semibold">
                  <span>Estimated Cost</span>
                  <span>{estimatedCreditCost} credits</span>
                </div>
              )}
            </div>

            {/* Quota Status */}
            {generationResult?.quotaStatus && (
              <div className="mt-3 space-y-2">
                <Separator />
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">API Usage Today</span>
                  <span>
                    {generationResult.quotaStatus.requestsToday} requests
                  </span>
                </div>
                {generationResult.quotaStatus.isLikelyQuotaLimited && (
                  <Alert
                    variant="destructive"
                    className="py-2"
                  >
                    <AlertTriangle className="h-3 w-3" />
                    <AlertDescription className="text-xs">
                      You may be hitting rate limits. Consider waiting before
                      retrying.
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}

            <Button
              type="button"
              className="mt-4 w-full"
              disabled={
                isSubmitting || formIsSubmitting || estimatedCreditCost === 0
              }
              onClick={onSubmit}
            >
              {isSubmitting || formIsSubmitting
                ? 'Generating...'
                : generationResult && !(isSubmitting || formIsSubmitting)
                  ? 'Generate Again ✧'
                  : 'Generate ✧'}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
